export type BoardDimension = {
  label: string;
  weight: number;
  score: number | null;
  confidence: number | null;
  status: string;
  summary: string;
};

export type BoardSource = {
  label: string;
  source: string;
  date: string;
  confidence: number;
};

export type BoardReportData = {
  location: string;
  county: string;
  stage: string;
  premises: string;
  eircode: string;
  objective: string;
  generatedAt: string;
  recommendation: string;
  decisionReady: boolean;
  score: number | null;
  confidence: number;
  dimensions: BoardDimension[];
  opportunities: string[];
  risks: string[];
  gaps: string[];
  forecast: Array<{ label: string; value: string }>;
  sources: BoardSource[];
};

const navy: [number, number, number] = [11, 48, 72];
const blue: [number, number, number] = [18, 63, 93];
const red: [number, number, number] = [197, 0, 78];
const muted: [number, number, number] = [100, 113, 122];
const pale: [number, number, number] = [238, 242, 244];

function safeFilename(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export async function exportBoardReportPdf(data: BoardReportData) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const pageWidth = 210;
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  let y = 18;

  function footer() {
    const page = doc.getCurrentPageInfo().pageNumber;
    doc.setDrawColor(220, 226, 230);
    doc.line(margin, 283, pageWidth - margin, 283);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    doc.text("NAMO Location Intelligence - Powered by Method Marketing", margin, 288);
    doc.text(`Page ${page}`, pageWidth - margin, 288, { align: "right" });
  }

  function pageHeader(section: string) {
    doc.setFillColor(...red);
    doc.rect(margin, 14, 2, 12, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.setTextColor(...blue);
    doc.text("NAMO", margin + 7, 23);
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text(section.toUpperCase(), pageWidth - margin, 22, { align: "right" });
    y = 35;
  }

  function newPage(section: string) {
    doc.addPage();
    pageHeader(section);
  }

  function ensureSpace(height: number, section = "Board report") {
    if (y + height > 278) newPage(section);
  }

  function wrapped(text: string, x: number, width: number, size = 9, color = navy, style: "normal" | "bold" = "normal") {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text || "Not provided", width) as string[];
    doc.text(lines, x, y);
    y += lines.length * (size * 0.42) + 2;
  }

  function sectionTitle(title: string, subtitle?: string) {
    ensureSpace(subtitle ? 18 : 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...navy);
    doc.text(title, margin, y);
    y += 6;
    if (subtitle) wrapped(subtitle, margin, contentWidth, 8, muted);
  }

  function bullets(items: string[], emptyText: string) {
    const values = items.length ? items : [emptyText];
    for (const item of values) {
      ensureSpace(12);
      doc.setFillColor(...red);
      doc.circle(margin + 1.5, y - 1.2, 0.8, "F");
      const lines = doc.splitTextToSize(item, contentWidth - 8) as string[];
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...navy);
      doc.text(lines, margin + 6, y);
      y += lines.length * 4 + 2;
    }
  }

  pageHeader("Board report");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...red);
  doc.text("LOCATION INTELLIGENCE", margin, y);
  y += 10;
  doc.setFontSize(25);
  doc.setTextColor(...navy);
  doc.text(doc.splitTextToSize(`${data.location}, ${data.county}`, contentWidth), margin, y);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...muted);
  doc.text(`${data.stage} assessment | Generated ${data.generatedAt}`, margin, y);
  y += 12;

  doc.setFillColor(...pale);
  doc.roundedRect(margin, y, contentWidth, 35, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text("OVERALL RECOMMENDATION", margin + 7, y + 9);
  doc.setFontSize(14);
  doc.setTextColor(...(data.decisionReady ? blue : red));
  const recommendationLines = doc.splitTextToSize(data.recommendation, 108) as string[];
  doc.text(recommendationLines.slice(0, 2), margin + 7, y + 17);
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text(data.decisionReady ? "Decision gates passed" : "Outstanding evidence prevents a final recommendation", margin + 7, y + 28);
  doc.setFontSize(24);
  doc.setTextColor(...navy);
  doc.text(data.score === null ? "-" : String(Math.round(data.score)), pageWidth - margin - 35, y + 21, { align: "center" });
  doc.setFontSize(7.5);
  doc.setTextColor(...muted);
  doc.text("SCORE", pageWidth - margin - 35, y + 28, { align: "center" });
  doc.setFontSize(17);
  doc.setTextColor(...blue);
  doc.text(`${data.confidence}%`, pageWidth - margin - 8, y + 21, { align: "right" });
  doc.setFontSize(7.5);
  doc.setTextColor(...muted);
  doc.text("CONFIDENCE", pageWidth - margin - 8, y + 28, { align: "right" });
  y += 46;

  sectionTitle("Location and brief");
  wrapped(`Premises: ${data.premises}`, margin, contentWidth, 9);
  wrapped(`Eircode: ${data.eircode}`, margin, contentWidth, 9);
  wrapped(`Objective: ${data.objective}`, margin, contentWidth, 9);
  y += 3;
  sectionTitle("Executive Summary");
  bullets(data.opportunities, "No verified positive evidence has been recorded yet.");
  y += 2;
  sectionTitle("Principal risks and conditions");
  bullets(data.risks, "No material risks have been recorded.");

  newPage("Dimension summaries");
  sectionTitle("Eight-dimension assessment", "Scores and confidence remain separate so weak evidence cannot hide behind a high potential score.");
  for (const dimension of data.dimensions) {
    ensureSpace(25, "Dimension summaries");
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(margin, y - 3, contentWidth, 22, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...navy);
    doc.text(dimension.label, margin + 5, y + 3);
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    doc.text(`${dimension.weight}% weight | ${dimension.status}`, margin + 5, y + 8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...blue);
    doc.text(dimension.score === null ? "-" : String(Math.round(dimension.score)), pageWidth - margin - 30, y + 5, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(...muted);
    doc.text("SCORE", pageWidth - margin - 30, y + 10, { align: "center" });
    doc.setFontSize(10);
    doc.setTextColor(...blue);
    doc.text(dimension.confidence === null ? "-" : `${Math.round(dimension.confidence)}%`, pageWidth - margin - 6, y + 5, { align: "right" });
    doc.setFontSize(7);
    doc.setTextColor(...muted);
    doc.text("CONFIDENCE", pageWidth - margin - 6, y + 10, { align: "right" });
    const summary = doc.splitTextToSize(dimension.summary, contentWidth - 70) as string[];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.7);
    doc.setTextColor(...navy);
    doc.text(summary.slice(0, 2), margin + 5, y + 14);
    y += 27;
  }

  newPage("Conditions and evidence");
  sectionTitle("Outstanding evidence and conditions");
  bullets(data.gaps, "No mandatory evidence gaps remain.");
  y += 4;
  sectionTitle("Commercial outlook");
  if (data.forecast.length) {
    for (const item of data.forecast) {
      ensureSpace(10);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...navy);
      doc.text(item.label, margin, y);
      doc.text(item.value, pageWidth - margin, y, { align: "right" });
      y += 7;
    }
  } else wrapped("No location forecast has been approved. Existing-store benchmarks must not be presented as a forecast for this premises.", margin, contentWidth, 8.5, muted);
  y += 5;
  sectionTitle("Evidence register", "Every source should be reviewed before this report is relied upon for a property or investment commitment.");
  const sources = data.sources.length ? data.sources : [{ label: "Evidence register", source: "No sources recorded", date: "-", confidence: 0 }];
  for (const source of sources) {
    ensureSpace(14, "Evidence register");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...navy);
    doc.text(doc.splitTextToSize(source.label, 72), margin, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...muted);
    doc.text(doc.splitTextToSize(source.source, 70), margin + 76, y);
    doc.text(source.date, pageWidth - margin - 20, y, { align: "right" });
    doc.text(`${source.confidence}%`, pageWidth - margin, y, { align: "right" });
    y += 11;
  }

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    footer();
  }
  doc.save(`NAMO-${safeFilename(data.location)}-board-report.pdf`);
}
